/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {h} from 'preact';
import {connect} from 'preact-redux';
import {PropTypes} from 'preact-compat';
import config from '../../config';
import styles from '../../../css/channelView.css';
import VideoPlayer from '../../phenix-video-player';
import FullScreenMessage from '../../shared/components/FullScreenMessage.jsx';
import LoadingScreen from '../../shared/components/LoadingScreen.jsx';
import NoSignalScreen from '../../shared/components/NoSignalScreen.jsx';
import AccessDeniedScreen from '../../shared/components/AccessDeniedScreen.jsx';

const View = ({
  mediaStream,
  auth,
  drmError,
  noTokenError,
  channelDoesNotExist,
  accessDenied,
  featureNotSupported,
  joinChannelPending,
  hideLoadingScreen,
  autoPlay,
  showNativeControls
}) => {
  if (drmError) {
    return (
      <FullScreenMessage
        messages={[
          'Unable to play protected content. This device is not supported for playback of protected content. Please update your browser and/or OS.'
        ]}
      />
    );
  }

  if (auth.isError) {
    return <FullScreenMessage messages={[`Error: ${auth.reason}`]} />;
  }

  if (noTokenError) {
    return <FullScreenMessage messages={[`Error: ${noTokenError}`]} />;
  }

  if (hideLoadingScreen && (joinChannelPending || !mediaStream)) {
    return <FullScreenMessage messages={['']} />;
  }

  if (joinChannelPending) {
    return <LoadingScreen />;
  }

  if (channelDoesNotExist) {
    return <FullScreenMessage messages={['Channel does not exist.']} />;
  }

  if (accessDenied) {
    return <AccessDeniedScreen />;
  }

  if (featureNotSupported) {
    return <FullScreenMessage messages={['Unsupported feature.']} />;
  }

  if (!mediaStream) {
    const message = config.offlinePage.disableText ? '' : config.offlinePage.text || '';

    return <NoSignalScreen mediaUri={config.offlinePage.imageUrl} message={message} />;
  }

  const storeLocalStateKey = config.audio.muted === undefined ? window.location.pathname + window.location.hash : null;
  const viewConfig = {align: config.video.align};

  return (
    <ul className={styles.list}>
      <li key={mediaStream ? mediaStream.getStreamId() : ''} className={styles.listItem}>
        <VideoPlayer
          id={mediaStream ? mediaStream.getStreamId() : ''}
          className={styles.video}
          stream={mediaStream}
          viewConfig={viewConfig}
          autoPlay={autoPlay || true}
          storeLocalStateKey={storeLocalStateKey}
          showNativeControls={showNativeControls}
        />
      </li>
    </ul>
  );
};

View.propTypes = {
  mediaStream: PropTypes.object.isRequired,
  auth: PropTypes.object,
  drmError: PropTypes.bool.isRequired,
  channelDoesNotExist: PropTypes.bool.isRequired,
  accessDenied: PropTypes.bool.isRequired,
  featureNotSupported: PropTypes.bool.isRequired,
  joinChannelPending: PropTypes.bool.isRequired,
  noTokenError: PropTypes.string,
  hideLoadingScreen: PropTypes.bool,
  alias: PropTypes.string,
  autoPlay: PropTypes.bool,
  showNativeControls: PropTypes.bool
};

const mapStateToProps = (state) => {
  return {
    auth: state.app.auth,
    drmError: !!state.app.channel.drmError,
    noTokenError: state.app.channel.noTokenError,
    channelDoesNotExist: state.app.channel.channelDoesNotExist,
    accessDenied: state.app.channel.accessDenied,
    featureNotSupported: state.app.channel.featureNotSupported,
    mediaStream: state.app.channel.mediaStream,
    joinChannelPending: state.app.channel.joinChannelPending,
    alias: state.app.channel.alias
  };
};

export default connect(mapStateToProps)(View);